import { MouseEvent, useEffect, useState } from 'react';
import { Box, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import CheckIcon from '@mui/icons-material/Check';

import SidebarLink from '@/components/shared/Sidebar/SidebarLink';
import { basicLinkStyles } from '@/components/shared/Sidebar/styles';
import { getTabAssignment } from '@/auth/activeAssignment';
import {
  getAssignmentsSnapshot,
  subscribeAssignments,
  switchAssignment,
} from '@/auth/roleSelection';
import { ApiProvider } from '@/provider/ApiProvider';

import { SuperAdminBadge } from './SuperAdminBadge';

/**
 * Role menu in the sidebar, above Settings (Authorization-Plan §13.6). Always
 * shown, also with a single role, so the user can see which role is active.
 * Opens like the profile menu; the active role carries a tick. Choosing
 * another role closes the menu, covers the page (RoleSwitchOverlay), POSTs it,
 * resets the role-dependent state, tells the other tabs and reloads the page.
 */
export const SidebarRoleMenu = () => {
  const [assignments, setAssignments] = useState(getAssignmentsSnapshot());
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const current = getTabAssignment();

  useEffect(() => subscribeAssignments(setAssignments), []);

  if (!current) return null;

  const label = current.label || 'Role';
  // the active role's level, from the switcher's own list (me/assignments `level`)
  const currentLevel = assignments.find((a) => a.id === current.id)?.level ?? null;

  const choose = async (id: number) => {
    setAnchorEl(null);
    if (id === current.id) return;
    const target = assignments.find((a) => a.id === id);
    if (!target) return;
    // the overlay covers the page from here until the reload (or the failure)
    const ok = await switchAssignment(target);
    if (!ok) {
      ApiProvider.globalToastMessage(
        'The role could not be switched. Please try again.',
        5000,
        'error',
      );
    }
  };

  return (
    <>
      {/* The click bubbles up from the sidebar link; the wrapper anchors the menu. */}
      <Box
        sx={{ position: 'relative' }}
        data-testid='sidebar-role-menu'
        aria-label='Switch role'
        onClick={(e: MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget)}
      >
        <SidebarLink
          icon={CorporateFareIcon}
          text={label}
          boxContainerStyles={basicLinkStyles}
          handleClick={() => undefined}
        />
        {/* the chip in the wide sidebar, a dot on the role icon at 65px (the chip does not
            fit); Sidebar.tsx shows one of them by the sidebar's real width (CSS :hover) */}
        <Box className='sidebar__wide-only' sx={{ pl: '40px', mt: '-4px' }}>
          <SuperAdminBadge level={currentLevel} testId='active-role-super-admin' />
        </Box>
        <Box
          className='sidebar__narrow-only'
          sx={{ position: 'absolute', top: 6, left: 34, lineHeight: 0 }}
        >
          <SuperAdminBadge level={currentLevel} testId='active-role-super-admin-dot' compact />
        </Box>
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        sx={{
          '.MuiPaper-root': {
            top: 'unset !important',
            bottom: '60px',
            borderRadius: '0',
            backgroundColor: 'primary.main',
            padding: '10px 20px 10px 10px',
            boxShadow: 'none',
            minWidth: 280,
          },
          left: '49px',
          zIndex: '1201',
        }}
      >
        <Typography sx={{ px: 2, pb: 1, fontSize: '12px', opacity: 0.7, color: 'typography.main' }}>
          Switch role
        </Typography>
        {(assignments.length ? assignments : []).map((a) => {
          const active = a.id === current.id;
          return (
            <MenuItem
              key={a.id}
              selected={active}
              onClick={() => choose(a.id)}
              data-testid={`role-option-${a.id}`}
              sx={{ color: 'typography.main', alignItems: 'flex-start' }}
            >
              <ListItemIcon sx={{ minWidth: 32, mt: '2px', color: 'typography.main' }}>
                {active ? <CheckIcon fontSize='small' data-testid='role-option-active' /> : null}
              </ListItemIcon>
              <ListItemText
                primary={
                  <>
                    {a.label || `Role ${a.id}`}
                    <SuperAdminBadge
                      level={a.level}
                      testId={`role-option-super-admin-${a.id}`}
                      dark
                    />
                  </>
                }
                slotProps={{
                  primary: { sx: { fontSize: '13px', fontWeight: active ? 600 : 400 } },
                }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default SidebarRoleMenu;
